import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { keys } from '../../api/keys'
import { AuthContext, type AuthContextValue } from '../../auth/AuthProvider'
import '../accounts/accounts.css'
import { Panel } from '../../platform/primitives/Panel'
import { ButtonPreview } from '../../platform/primitives/ButtonPreview'
import { Button } from '../../platform/primitives/Button'
import { PremiumGate } from '../../platform/access/PremiumGate'
import { EntitlementsProvider } from '../../platform/access/useEntitlement'
import { AppCrashed } from '../errors/AppCrashed'
import { Maintenance } from '../errors/Maintenance'
import { NotFound } from '../errors/NotFound'
import { VerifyEmail } from '../auth/VerifyEmail'
import { CategoryTable } from '../categories/CategoryTable'
import { CategoriesToolbar } from '../categories/CategoriesToolbar'
import { useCategoryView } from '../categories/useCategoryView'
import { CategoryForm } from '../categories/CategoryForm'
import type { Category } from '../categories/categoryTree'
import { MemberRow } from '../household/MemberRow'
import { toMember } from '../household/useMembers'
import { InvitationRow } from '../household/InvitationRow'
import { InviteDialog } from '../household/InviteDialog'
import { toInvitation } from '../household/useInvitations'
import type { components } from '../../api/generated/schema'
import { RuleRow, rowLayoutFor } from '../rules/RuleRow'
import { fromResponse, toWrite, type Rule, type RuleDraft } from '../rules/ruleWire'
import type { Merchant } from '../merchants/useMerchants'
import { RuleBuilder } from '../rules/RuleBuilder'
import { RuleSuggestions } from '../rules/RuleSuggestions'
import type { PreviewResult, RuleSuggestion, SuggestionSample } from '../rules/useRules'
import type { Account } from '../accounts/useAccounts'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { SearchField } from '../../platform/primitives/SearchField'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { CashflowScreen } from '../cashflow/CashflowScreen'
import { CashflowDetailScreen } from '../cashflow/CashflowDetailScreen'
import { toTxn } from '../transactions/transactionWire'
import {
  cashflowCategoriesFixture,
  cashflowFreePeriodProbeFixture,
  cashflowHistoryProbeFixture,
  cashflowPeriodProbeFixture,
  CASHFLOW_FREE_FIXTURE,
  CASHFLOW_NEW_ACCOUNT_FIXTURE,
} from '../cashflow/cashflowFixture'
import { unitStart, type CashflowView } from '../cashflow/cashflowPeriod'
import { detailPath } from '../cashflow/cashflowScope'
import { largestSameDescription } from '../cashflow/sameDescription'
import { SameDescriptionPrompt } from '../cashflow/SameDescriptionPrompt'
import { toCashflow, type Cashflow, type CashflowWire } from '../cashflow/cashflowWire'
import type { AsyncState } from '../../platform/async/Async'
import type { CashflowResult } from '../cashflow/useCashflow'
import './probe.css'

/**
 * A dev-only door onto the screens no browser can otherwise reach.
 *
 * **Why this exists.** Six defects in this module were found by a person
 * looking at a screenshot, not by the suite: double page padding, a factor
 * label printed twice, native blue radios, a card that shrank to its content,
 * four links underlined inside filled pills, and a 48px icon rendering below
 * its own heading. Every one was invisible to 1838 unit tests, because jsdom
 * computes no layout and none of them are logic errors. They were invisible to
 * the browser sweeps too — for the plainest possible reason: **the screens are
 * unreachable.** `/verify-email` needs a session, the 404 needs a bad URL
 * *behind* auth, the crash screen needs a thrown error, and `Maintenance` needs
 * props no route supplies.
 *
 * A guard that cannot see a screen is not protecting it. This is the door.
 *
 * **`import.meta.env.DEV`, checked in `App.tsx` where the route is declared.**
 * Vite replaces that expression with a literal at build time, so the whole
 * subtree — this file, its fixtures, and the `AuthContext` stub below — is dead
 * code in a production bundle and is dropped. `probe.build.test.ts` asserts
 * that rather than trusting it: it greps a real production build for the
 * sentinel below and requires it absent. An auth bypass that ships is a
 * security defect, not a tidiness one, so it is checked rather than assumed.
 *
 * The sentinel is deliberately plain ASCII. The first draft contained a
 * Cyrillic \u0435 in "track", which looks identical in an editor — a grep for
 * the ASCII spelling would have matched nothing and the build test would have
 * passed while proving nothing at all.
 */
export const PROBE_SENTINEL = 'TRACKE5_PROBE_ONLY_DEV_BUILD'

/**
 * A parent, its children, a null colour, the longest seeded name, and a user
 * category — Task 12's list, `M11-R21` in view.
 *
 * **Fixed, never live.** A probe that renders real data makes two screenshots
 * differ for reasons unrelated to the change being measured, and this fixture
 * is what the browser sweeps below 640px actually photograph.
 *
 * `var(--s1)`/`var(--s3)`/`var(--s5)` — real tokens, not hex literals. The
 * colour guard scans `src/**` with no test-file exemption, and this file is
 * shipped source (dropped from a production build by `import.meta.env.DEV`,
 * not by never having been written).
 *
 * Every field `CategoryResponse` requires is present, and the optional ones
 * are filled in rather than omitted — a hand-built fixture that compiled only
 * because TypeScript let a field go missing would prove nothing about the
 * component that reads the real, always-fully-populated API response.
 *
 * **That promise lapsed twice.** Backend 270 added `key` and `icon_paths`,
 * and backend #401 then replaced `icon_paths`, `web_icon`, `sf_icon` and
 * `material_icon` with `icon` (a catalogue slug) and `icon_url`. Each time
 * the fixture went on compiling for a while and photographed a code path the
 * real screen no longer uses. It now carries #401's shape.
 *
 * **The `icon_url`s are real catalogue URLs**, the cover icons
 * `/category-icons/groups` returns, so the sweeps photograph the same
 * `<img>` the real screen draws. Two rows carry `icon_url: null`, because the
 * real API sends it there: `Coffee Shops` keeps a legacy bundled slug the
 * catalogue does not know (the bundled-glyph fallback), and the user-created
 * `Side Hustle` has no icon at all (the `Uncategorized` fallback).
 */

/** A catalogue icon's URL, the shape `icon_url` takes on the wire. */
const iconUrl = (slug: string) => `https://icons.tracke5.com/icons/${slug}.svg`

const CATEGORIES_FIXTURE: Category[] = [
  {
    id: 'fixture-food',
    // The renamed wording. `Food & Dining` is what the pack is keyed by and
    // what `RENAMED` maps back to; the API says this.
    name: 'Food & Drinks',
    key: 'food',
    color: 'var(--s1)',
    parent_id: null,
    is_system: true,
    is_active: true,
    display_order: 1,
    category_type: 'expense',
    icon: 'fork-knife-duotone',
    icon_url: iconUrl('fork-knife-duotone'),
    description: null,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 'fixture-groceries',
    name: 'Groceries',
    key: 'food.groceries',
    color: 'var(--s1)',
    parent_id: 'fixture-food',
    is_system: true,
    is_active: true,
    display_order: 2,
    category_type: 'expense',
    icon: 'shopping-cart-duotone',
    icon_url: iconUrl('shopping-cart-duotone'),
    description: null,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 'fixture-restaurants',
    name: 'Restaurants',
    key: 'food.restaurants',
    color: 'var(--s1)',
    parent_id: 'fixture-food',
    is_system: true,
    is_active: true,
    display_order: 3,
    category_type: 'expense',
    icon: 'hamburger-duotone',
    icon_url: iconUrl('hamburger-duotone'),
    description: null,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    // A colour-less child — kept null, though it renders no differently from
    // its coloured siblings any more: #454 removed category colour from the
    // web app entirely, so this fixture is here for the icon fallback below,
    // not for a swatch that no longer exists.
    id: 'fixture-coffee',
    name: 'Coffee Shops',
    key: 'food.coffee-shops',
    color: null,
    parent_id: 'fixture-food',
    is_system: true,
    is_active: true,
    display_order: 4,
    category_type: 'expense',
    // A legacy bundled slug: not in the catalogue, so `icon_url` is null and
    // the row draws the bundled glyph.
    icon: 'coffee-shops',
    icon_url: null,
    description: null,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    // The longest name in the seed, and a second, populated segment —
    // `SectionHeader` "Income" appears alongside "Expense" as a result.
    id: 'fixture-retirement',
    name: 'Retirement & Pension',
    key: 'income.retirement-and-pension',
    color: 'var(--s3)',
    parent_id: null,
    is_system: true,
    is_active: true,
    display_order: 1,
    category_type: 'income',
    icon: 'piggy-bank-duotone',
    icon_url: iconUrl('piggy-bank-duotone'),
    description: null,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
  {
    // The one row that is not system-owned, so its `Edit Side Hustle` control
    // is in the sweep's hit-target measurements — `.categories__edit` has no
    // `useHitTarget`-driven floor of its own (Task 9's review, carried here).
    id: 'fixture-user',
    name: 'Side Hustle',
    // `null` on both, and that is the real shape rather than a gap: a
    // user-created category has no `key` (keys name seeded rows) and no
    // `icon_url`, and its name is in no bundled glyph either, so this row
    // photographs the `Uncategorized` fallback.
    key: null,
    icon_url: null,
    color: 'var(--s5)',
    parent_id: null,
    is_system: false,
    is_active: true,
    display_order: 5,
    category_type: 'expense',
    icon: null,
    description: null,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  },
]

/**
 * `CategoryTable` against the fixed fixture above, in the same `Panel` the
 * real `/categories` screen wraps it in — the sweeps measure the box the rows
 * actually sit in, not a bare, unwrapped tree.
 *
 * **Points at the TABLE, not the list it replaced.** The route swapped
 * components and this fixture did not, for one commit — which would have left
 * every browser sweep measuring a component no route renders while reporting
 * green on the categories screen. That is worse than no probe: it is a sweep
 * that looks like coverage.
 *
 * `onEdit`/`onDelete`/`onActivate` all throw, same shape as `unavailable` below: there
 * is no form and no route behind this fixture, so a click that silently
 * succeeded would imply a wired flow that is not there.
 */
function CategoriesFixture() {
  // The tree, the filter and the sort moved into `useCategoryView` on
  // 2026-08-21 so the route could render one toolbar in every Async state.
  // The probe calls the same hook against its FIXED rows, so what it
  // photographs is still derived by the code the screen uses — a fixture that
  // built its own tree here would drift from the real one silently.
  // **This buys the sweeps nothing today, and that is a gap in the
  // sweeps rather than here.** The docblock above says this fixture is
  // what they photograph; no harness in `e2e/` navigates to `/__probe`
  // at all. `hit-targets` reaches `/categories` through the sidebar and
  // measures the real screen against a sweep account with no
  // transactions — so it sees `most_used: []` and the segment correctly
  // absent, and this fixture's ranking is never rendered by anything
  // automated. Recorded in the M11 migration spec.

  const view = useCategoryView(CATEGORIES_FIXTURE, ['fixture-user', 'fixture-groceries'])
  return (
    <Panel>
      <CategoriesToolbar view={view} />
      <CategoryTable
        view={view}
        onEdit={unavailable('onEdit')}
        onDelete={unavailable('onDelete')}
        onActivate={unavailable('onActivate')}
        // FIXED, like everything else here, and non-empty on purpose: the
        // `Most used` segment is hidden when there is no ranking, so a probe
        // that passed nothing would render one control fewer than the real
        // screen. Two ids, so the segment sorts something rather than merely
        // appearing — `fixture-groceries` is a CHILD, which is the case the
        // subtree ranking exists for.
        //
        // The ORDER of these two is load-bearing and was wrong once. With
        // `fixture-groceries` first, the ranking agrees with A–Z (its parent
        // `Food & Drinks` sorts before `Side Hustle` either way) and pressing
        // the segment visibly does nothing — a probe that renders the control
        // but cannot show it working. Reversed, the two orders disagree.
        //
      />
    </Panel>
  )
}

type CashflowProbeKind = 'premium' | 'free' | 'new' | 'empty' | 'no-accounts' | 'error'

/**
 * `/cashflow` from the M3 fixtures, with no fetch behind it. Every body is
 * derived HERE, inside the component, never at module scope (an eager map ships
 * a probe fixture). The controls work — they change the view the screen draws —
 * but the data is fixed: the history is the fixture's own series, and the
 * period is the fixture's totals whichever period is selected. `error` is a
 * real `CashflowContractError` from a float amount.
 */
function CashflowFixture({ kind }: { kind: CashflowProbeKind }) {
  const wire = useMemo<CashflowWire>(
    () =>
      kind === 'premium'
        ? cashflowPeriodProbeFixture()
        : kind === 'new'
          ? CASHFLOW_NEW_ACCOUNT_FIXTURE
          : kind === 'no-accounts'
            ? { ...CASHFLOW_FREE_FIXTURE, scope: { account_count: 0, shared_account_count: 0 } }
            : kind === 'empty'
              ? {
                  ...CASHFLOW_FREE_FIXTURE,
                  totals: {
                    ...CASHFLOW_FREE_FIXTURE.totals,
                    income: '0.00',
                    expense: '0.00',
                    transfers_in: '0.00',
                    transfers_out: '0.00',
                    net: '0.00',
                    deficit_bucket_count: 0,
                    deficit_buckets: [],
                  },
                }
              : cashflowFreePeriodProbeFixture(),
    [kind],
  )
  const [view, setView] = useState<CashflowView>(() => ({
    granularity: 'month',
    period: unitStart(wire.window.today, 'month'),
  }))
  const entitled = kind === 'premium' || kind === 'new'
  const period = useMemo<CashflowResult>(() => {
    const { buckets: _buckets, ...body } = wire
    let state: AsyncState<Cashflow>
    if (kind === 'error') {
      try {
        toCashflow({ ...body, totals: { ...body.totals, income: 64230 } })
        state = { status: 'error', message: 'The probe expected a contract error.' }
      } catch {
        state = { status: 'error', message: "Cashflow couldn't be loaded." }
      }
    } else {
      state = { status: 'success', data: toCashflow({ ...body, window: { ...body.window, granularity: view.granularity } }) }
    }
    return { state, retry: unavailable('retry'), fetching: false }
  }, [kind, wire, view.granularity])
  const history = useMemo<CashflowResult | null>(() => {
    // The history is its own body (the period call carries no buckets), so the chart has a year or two to draw.
    const body = kind === 'premium' ? cashflowHistoryProbeFixture() : wire
    if (!entitled || body.buckets === undefined) return null
    const data = toCashflow({ ...body, window: { ...body.window, granularity: view.granularity } })
    return { state: { status: 'success', data }, retry: unavailable('retry'), fetching: false }
  }, [entitled, kind, wire, view.granularity])
  return (
    <EntitlementsProvider granted={entitled ? ['cashflow_details'] : []}>
      <CashflowScreen
        view={view}
        today={wire.window.today}
        entitled={entitled}
        history={history}
        period={period}
        categories={cashflowCategoriesFixture()}
        onViewChange={(next) => setView(next)}
        // The link navigates on its own; the probe has no analytics to emit.
        onMerchantsExpanded={() => {}}
        rowLink={(t) => detailPath(t, view, wire.window.today)}
      />
    </EntitlementsProvider>
  )
}

/**
 * `/cashflow/category/groceries` from the M3 fixtures, no fetch behind it: the
 * drill-down's layout with figures in it, which the empty sweep account cannot
 * give. The scoped period is the free body re-figured to one row; the chart is
 * the premium history; four transactions over two days. Everything is derived
 * here, inside the component (an eager map ships a probe fixture).
 */
function CashflowDetailFixture({ flagged = false, noMerchant = false }: { flagged?: boolean; noMerchant?: boolean }) {
  const today = '2026-10-13'
  const [view, setView] = useState<CashflowView>(() => ({ granularity: 'month', period: '2026-09-01' }))
  const period = useMemo<CashflowResult>(() => {
    const { buckets: _buckets, ...body } = cashflowFreePeriodProbeFixture()
    const wire: CashflowWire = {
      ...body,
      window: { ...body.window, granularity: view.granularity },
      totals: { ...body.totals, expense: flagged ? '-480.00' : '204.00', income: '12.00' },
      prior: body.prior === undefined || body.prior === null ? null : { ...body.prior, totals: { ...body.prior.totals, expense: '187.00' } },
    }
    return { state: { status: 'success', data: toCashflow(wire) }, retry: unavailable('retry'), fetching: false }
  }, [flagged, view.granularity])
  const history = useMemo<CashflowResult>(() => {
    const body = cashflowHistoryProbeFixture()
    const data = toCashflow({ ...body, window: { ...body.window, granularity: view.granularity } })
    return { state: { status: 'success', data }, retry: unavailable('retry'), fetching: false }
  }, [view.granularity])
  const rows = useMemo(() => {
    // `brand`: the merchant entity a row is assigned to, or none — only such a row carries "Needs merchant".
    const row = (id: string, merchant: string, amount: string, date: string, brand?: string): components['schemas']['TransactionResponse'] => ({
      id,
      ...(brand === undefined ? {} : { merchant_id: `m-${id}`, merchant: { id: `m-${id}`, name: brand, type: 'business', is_global: false } }),
      account_id: 'acc-chq',
      name: merchant.toUpperCase(),
      merchant_name: merchant,
      amount,
      transaction_date: date,
      iso_currency_code: 'CAD',
      category_id: 'groceries',
      is_pending: false,
      is_removed: false,
      is_excluded: false,
      tags: [],
      created_at: `${date}T00:00:00Z`,
      updated_at: `${date}T00:00:00Z`,
      review_status: 'not_required',
      category_source: 'user',
    })
    if (noMerchant) {
      // No merchant on any row; three share one long bank description, so the "Set all" prompt shows and must wrap.
      const sq = 'Sq *blue bottle coffee 0423 san francisco ca'
      return [
        row('t1', sq, '6.25', '2026-09-26'),
        row('t2', 'Etransfer 88231 a smith', '40.00', '2026-09-26'),
        row('t3', sq, '5.75', '2026-09-19'),
        row('t4', sq, '7.10', '2026-09-14'),
      ].map(toTxn)
    }
    return [
      row('t1', 'Whole Foods', '58.20', '2026-09-26', 'Whole Foods Market'),
      row('t2', 'Whole Foods refund', '-12.00', '2026-09-26'),
      // No merchant, and a long name: the sweeps measure a stacked Merchant cell that must ellipsise, not widen.
      row('t3', 'Loblaws Great Food Superstore with a long trading name that must truncate', '91.45', '2026-09-14'),
      row('t4', 'No Frills', '66.35', '2026-09-14', 'No Frills'),
    ].map(toTxn)
  }, [noMerchant])
  const accounts = useMemo(() => new Map([['acc-chq', { name: 'Chequing', mask: '4412' }]]), [])
  const group = noMerchant ? largestSameDescription(rows) : null
  const categories = useMemo(() => new Map([['groceries', 'Groceries']]), [])
  return (
    <CashflowDetailScreen
      scope={noMerchant ? { kind: 'no_merchant' } : { kind: 'category', id: 'groceries' }}
      side="out"
      name={noMerchant ? { status: 'ready', text: 'No merchant', icon: null } : { status: 'ready', text: 'Groceries', icon: { name: 'Groceries', icon: 'groceries', icon_url: null } }}
      view={view}
      today={today}
      earliest="2024-11-01"
      period={period}
      history={history}
      share={flagged || noMerchant ? null : { pct: 36, expenseCents: 57300, currency: 'CAD' }}
      transactions={{ status: 'success', data: { rows, total: rows.length, page: 1, hasMore: false }, fetching: false }}
      accounts={accounts}
      categories={categories}
      categoriesStatus="success"
      crumbHref="/cashflow?period=2026-09-01"
      onViewChange={(next) => setView(next)}
      onPage={unavailable('page')}
      // Both row pills drawn, as the live page draws them: the sweeps measure their layout and hit areas.
      onCategoryClick={unavailable('categoryClick')}
      onMerchantClick={unavailable('merchantClick')}
      prompt={group === null ? null : <SameDescriptionPrompt group={group} onChoose={unavailable('choose')} />}
    />
  )
}

/** One entry per screen. `render` is a thunk so nothing mounts until asked. */
/**
 * The new-category dialog, open, against the same fixture.
 *
 * `onClose` throws like every other fixture handler — there is no state behind
 * this screen to close into, and a close that silently did nothing would imply
 * a wired flow that is not here.
 */
function CategoryFormFixture() {
  // Built here, not at module scope (an eager map ships the fixture). "TV" is
  // a two-letter name, the shortest label the chip's width floor can be measured on.
  const top = CATEGORIES_FIXTURE.filter((c) => c.parent_id == null)
  const short: Category = { ...top[0]!, id: 'fixture-tv', name: 'TV', key: 'tv' }
  return (
    <CategoryForm
      onClose={unavailable('onClose')}
      parents={[...top, short]}
      initialNested
    />
  )
}

/**
 * Every variant and size a `PremiumGate` caller previews (#578), each as the LIVE
 * `Button` and its `ButtonPreview` side by side, in block wrappers carrying
 * `data-pair` and `data-kind`. A browser spec measures the box AND the label
 * offset of the real components, so the preview cannot drift from the control
 * it stands in for. Variant/size pairs: primary/page (Categories, Merchants,
 * Connect link), outline/page (Accounts, Connect manual, Export), chip/panel
 * (Reconnect).
 */
const PREVIEW_PAIRS = [
  { pair: 'primary-page', variant: 'primary', size: 'page', label: 'New category' },
  { pair: 'outline-page', variant: 'outline', size: 'page', label: 'Add manually' },
  { pair: 'chip-panel', variant: 'chip', size: 'panel', label: 'Reconnect' },
] as const

/** Connect's two buttons, in the order and variants `Connect.tsx` renders them. */
const CONNECT_PAIR = [
  { key: 'link', variant: 'primary', label: 'Link a bank' },
  { key: 'manual', variant: 'outline', label: 'Add manually' },
] as const

function ButtonPreviewFixture() {
  return (
    <Panel>
      {PREVIEW_PAIRS.map((p) => (
        <div key={p.pair} className="probe-pair" data-pair={p.pair}>
          <div data-kind="live">
            <Button variant={p.variant} size={p.size}>
              {p.label}
            </Button>
          </div>
          <div data-kind="preview">
            <ButtonPreview variant={p.variant} size={p.size}>
              {p.label}
            </ButtonPreview>
          </div>
        </div>
      ))}
      {/* Connect's stacked PAIR (#586): the live pair in its Panel, beside the preview
          pair seated in a real locked `PremiumGate` exactly as `Connect.tsx` does, so a
          change to the gate's wrapper moves what the browser measures here. */}
      <div className="probe-pair probe-pair--stacked" data-pair="connect-stack">
        <div data-kind="live">
          <Panel className="accounts-stack">
            {CONNECT_PAIR.map((p) => (
              <Button key={p.key} variant={p.variant} size="page">
                {p.label}
              </Button>
            ))}
          </Panel>
        </div>
        <div data-kind="preview">
          <Panel>
            <EntitlementsProvider granted={[]}>
              <PremiumGate
                entitlement="unlimited_accounts"
                onUpgrade={unavailable('onUpgrade')}
                preview={
                  <div className="accounts-stack-group">
                    {CONNECT_PAIR.map((p) => (
                      <ButtonPreview key={p.key} variant={p.variant} size="page">
                        {p.label}
                      </ButtonPreview>
                    ))}
                  </div>
                }
              >
                <Button variant="primary" size="page">
                  Link a bank
                </Button>
              </PremiumGate>
            </EntitlementsProvider>
          </Panel>
        </div>
      </div>
      {/* ExportButton's real context: the control is itself a flex item of an
          `inline-flex` column (`.txn-export`), so it is blockified — a different
          layout path from the block wrappers above. */}
      <div className="probe-pair" data-pair="outline-page-in-flex-column">
        <div className="probe-pair__column" data-kind="live">
          <Button variant="outline" size="page">
            Export CSV
          </Button>
        </div>
        <div className="probe-pair__column" data-kind="preview">
          <ButtonPreview variant="outline" size="page">
            Export CSV
          </ButtonPreview>
        </div>
      </div>
    </Panel>
  )
}

/**
 * The locked gate, over the exact preview `Categories.tsx` passes it.
 *
 * **`EntitlementsProvider granted={[]}` is the whole point.** In development
 * `TIER` resolves to `'premium'`, so `entitlementsForTier` returns all seven
 * and the locked branch of every gate in the app is dead code to a browser —
 * no sweep, no screenshot, no developer glance has ever rendered it. An empty
 * provider here overrides that for this one subtree.
 *
 * The preview is copied from `Categories.tsx` rather than invented, because the
 * defect was a function of the preview's WIDTH: the overlay is `inset: 0`, so a
 * narrower preview than the CTA is what produced a four-line label. A fixture
 * with a roomier preview would render correctly and prove nothing.
 */
function PremiumGateFixture() {
  return (
    <Panel>
      <div className="categories__header">
        <div className="categories__heading">
          <h2 className="categories__title">Categories</h2>
          <p className="categories__subtitle">262 categories</p>
        </div>
        <EntitlementsProvider granted={[]}>
          <PremiumGate
            entitlement="custom_categories"
            preview={
              <ButtonPreview variant="primary" size="page">
                New category
              </ButtonPreview>
            }
            onUpgrade={unavailable('onUpgrade')}
          >
            <Button variant="primary" size="page">
              New category
            </Button>
          </PremiumGate>
        </EntitlementsProvider>
      </div>
    </Panel>
  )
}

/**
 * Five members — issue 329, filed against #311's own card once `web-architect`
 * rendered the real screen and found two defects 59 unit tests and five clean
 * sweeps had missed. `test:overflow` genuinely visits `/household`, but with
 * no API reachable the route renders `<Async>` skeletons — a covered ROUTE
 * with no rendered CONTENT, so no sweep has ever measured a member row. This
 * fixture is what makes the row real for the sweeps to photograph.
 *
 * **Wire-shaped, not `Member`-shaped, and every field `MembershipResponse`
 * declares is present** — including the two optional ones, `user_email` and
 * `user_display_name`, both `null` on at least one row below rather than
 * omitted. `CategoriesFixture`'s own docblock records why: a fixture that
 * compiles with an optional field missing silently exercises the fallback
 * path forever, even after the real screen stops taking it. `status` is
 * carried too though nothing downstream reads it — the server sends it on
 * every row (`useMembers.ts`'s own docblock: the server, not this client,
 * filters to `ACTIVE`), so a fixture without it would not be the same shape.
 *
 * Mapped to `Member` with `useMembers.ts`'s own exported `toMember` — the
 * SAME transform `listMembers` applies, called rather than repeated (issue
 * #329, architect finding N1; it used to be five duplicated lines here,
 * un-unit-testable except through a mocked fetch). **No client-side sort
 * applied either**, matching the real screen: the server orders
 * role-descending then `joined_at`, and this array's own order is what
 * renders, unreordered.
 */
const MEMBERSHIP_FIXTURE: components['schemas']['MembershipResponse'][] = [
  {
    id: 'fixture-membership-owner',
    household_id: 'fixture-household',
    user_id: 'fixture-user-priya',
    user_email: 'priya.chen@example.com',
    user_display_name: 'Priya Chen',
    role: 'owner',
    status: 'active',
    joined_at: '2025-03-01T09:00:00Z',
    updated_at: '2025-03-01T09:00:00Z',
  },
  {
    // The overflow row. `user_display_name: null` is not an edge case — the
    // field is nullable on the wire — and the fallback label is this long
    // address, which is the exact shape that measured `document.scrollWidth`
    // 460px against a 320px viewport (issue 329, from #311's own finding).
    id: 'fixture-membership-long-email',
    household_id: 'fixture-household',
    user_id: 'fixture-user-long-email',
    user_email:
      'alexandra.montgomery-worthington.richardson@international-consulting-partners.example.com',
    user_display_name: null,
    role: 'admin',
    status: 'active',
    joined_at: '2025-04-12T14:30:00Z',
    updated_at: '2025-04-12T14:30:00Z',
  },
  {
    // The longest plausible NAME, as distinct from the row above's longest
    // EMAIL — a hyphenated name wraps differently than an unbroken address,
    // and the card asks for both shapes measured separately.
    id: 'fixture-membership-long-name',
    household_id: 'fixture-household',
    user_id: 'fixture-user-long-name',
    user_email: 'amfws@example.com',
    user_display_name: 'Anne-Marie Fitzgerald-Whitmore-Saint-Clair',
    role: 'member',
    status: 'active',
    joined_at: '2025-05-20T11:15:00Z',
    updated_at: '2025-05-20T11:15:00Z',
  },
  {
    // Outside the four-role enum on purpose — issue 306's client-side half.
    // `narrowRole('co_owner')` returns `null`, so `MemberRow` falls back to
    // `rawRole` and renders "co_owner" verbatim rather than defaulting it,
    // and `isLastOwner` (`roles.ts`) treats this member as neither an owner
    // nor a safe non-owner: it fails toward BLOCKING the three actions that
    // guard against removing the last owner, never toward permitting them.
    id: 'fixture-membership-unrecognised-role',
    household_id: 'fixture-household',
    user_id: 'fixture-user-diego',
    user_email: 'diego.ramirez@example.com',
    user_display_name: 'Diego Ramirez',
    role: 'co_owner',
    status: 'active',
    joined_at: '2025-06-02T08:45:00Z',
    updated_at: '2025-06-02T08:45:00Z',
  },
  {
    // Both nullable identity fields null at once — `MemberRow`'s label falls
    // all the way through to "Unnamed member" and `PersonAvatar` renders the
    // `?` chip, the one branch of `initialsFor` neither a name nor an email
    // fixture row above reaches.
    id: 'fixture-membership-unnamed',
    household_id: 'fixture-household',
    user_id: 'fixture-user-unnamed',
    user_email: null,
    user_display_name: null,
    role: 'viewer',
    status: 'active',
    joined_at: '2025-07-18T16:00:00Z',
    updated_at: '2025-07-18T16:00:00Z',
  },
]

/**
 * The five rows above, in the same `Panel` `Household.tsx` puts the member
 * list in. **No `<Async>` here**, matching `CategoriesFixture`'s own
 * precedent above — but not because `Async`'s success branch is a plain
 * pass-through (issue #329, architect finding N2: it is not — `Async.tsx`
 * always renders an offscreen live-region `<span>` plus a real `<div
 * aria-busy>` wrapping `children`, by explicit design, issue 264). The right
 * reason is that there is nothing here for `<Async>` to guard: this data is
 * FIXED, not a query, so there is no loading/error/stale to reach, and
 * `household-members` is a `live` surface (`useMembers.ts`), so a `stale`
 * render would throw rather than pass silently. `MemberRow` also takes no
 * callback props (it is inert — no role menu, no remove, no leave until
 * Tasks 5–7, `household.css`'s own docblock), so there is nothing for
 * `unavailable()` to guard either.
 *
 * Consequence of skipping it: these rows are direct flex children of
 * `.surface-box` (`Panel`, `gap: 14px`), where the real `/household` screen
 * (once Task 5-7 render its list through `<Async>`) makes them children of
 * that `aria-busy` `<div>` instead. Neither sweep this probe screen backs
 * sees the difference — both measure horizontally and the row width is
 * identical either way — but a future vertical-spacing check would not be
 * measuring the same box the real screen renders.
 *
 * **The `MembershipResponse` → `Member` map happens IN HERE, not at module
 * scope.** It did once, as `const HOUSEHOLD_MEMBERS_FIXTURE = MEMBERSHIP_
 * FIXTURE.map(...)` above this function — a top-level expression whose
 * callback called `narrowRole`, which Rollup cannot prove pure, so it kept
 * the expression and with it the whole `MEMBERSHIP_FIXTURE` array in the
 * production bundle (issue #329, architect finding B1): five real addresses
 * and a household id, unreachable but shipped. Computed inside the component
 * instead, the map runs only when `HouseholdMembersFixture` is called, which
 * only `import.meta.env.DEV`-gated `App.tsx` ever does — no annotation to
 * remember, nothing for a future fixture author to forget. Still true with
 * `toMember` as the callback (N1, below) rather than an inline object
 * literal: what matters is the statement's SCOPE, not the callback's shape.
 * `probe.build.test.ts` proves it: `fixture-` (both fixtures' own id prefix)
 * is now in `MARKERS`.
 */
function HouseholdMembersFixture() {
  const members = MEMBERSHIP_FIXTURE.map(toMember)
  return (
    <Panel>
      {members.map((m) => (
        <MemberRow key={m.id} member={m} />
      ))}
    </Panel>
  )
}

/**
 * Four invitations — issue #315's own final-pass gap, filed for the same
 * reason issue #329 was: this panel has never been rendered with content by
 * any sweep. `test:overflow` visits `/household`, but with no API reachable
 * the route renders `<Async>` skeletons, never an invitation row — a covered
 * ROUTE with no rendered CONTENT. This fixture is what makes a row real for
 * the sweeps to photograph, and it matters more here than it did for members:
 * an email, a role label, AND a timing phrase share one row, which is a
 * worse overflow risk than the member row's two columns.
 *
 * **Wire-shaped, not `Invitation`-shaped, and every field `InvitationResponse`
 * declares is present** — `household_name`, `token` and `code` included,
 * even though `toInvitation` drops all three (`useInvitations.ts`'s own
 * docblock). `MEMBERSHIP_FIXTURE`'s own docblock records why a fixture
 * compiling with an optional field simply absent proves nothing about the
 * mapper actually discarding it, as opposed to never having had it to drop in
 * the first place. `token`/`code` are set to a real-looking value rather than
 * `null` for exactly that reason, even though this endpoint always sends
 * `null` for both in practice (the schema's own description: populated only
 * on creation).
 *
 * Mapped to `Invitation` with `useInvitations.ts`'s own exported
 * `toInvitation` — the SAME transform `listInvitations` applies, following
 * `toMember`'s precedent above rather than repeating the five-line map.
 */
const INVITATION_FIXTURE: components['schemas']['InvitationResponse'][] = [
  {
    // The ordinary case: pending, a plausible email, and an expiry far
    // enough ahead that this row never flips to "Expired" just from real
    // wall-clock time passing while someone is looking at this screen.
    id: 'fixture-invitation-pending',
    household_id: 'fixture-household',
    household_name: 'The Mehtas',
    email: 'noor.abdullah@example.com',
    role: 'admin',
    status: 'pending',
    created_at: '2026-08-10T09:00:00Z',
    expires_at: '2099-01-01T00:00:00Z',
    invited_by_user_id: 'fixture-user-priya',
    token: 'wire-token-should-be-discarded',
    code: 'WIRE-CODE',
  },
  {
    // The B1 shape, not the naive one: `status: 'pending'` with a PAST
    // `expires_at`, never `status: 'expired'`. `cleanup_expired_invitations`
    // (backend `invitation_service.py:577`) is defined once and called from
    // nowhere, so `status` never actually reaches `'expired'` in production —
    // a fixture using that value instead would exercise a branch
    // `InvitationRow` cannot reach from a real API response. `isExpired`
    // (`useInvitations.ts`) is what has to derive this row's "Expired" label
    // from the date, not the field.
    id: 'fixture-invitation-expired',
    household_id: 'fixture-household',
    household_name: 'The Mehtas',
    email: 'sam.okonkwo@example.com',
    role: 'viewer',
    status: 'pending',
    created_at: '2026-07-01T09:00:00Z',
    expires_at: '2026-08-08T00:00:00Z',
    invited_by_user_id: 'fixture-user-priya',
    token: null,
    code: null,
  },
  {
    // Outside the three invitable roles on purpose — `narrowRole('co_owner')`
    // returns `null`, so `InvitationRow` falls back to `rawRole` and renders
    // "co_owner" verbatim, the invitation-panel half of issue 306's pattern
    // (`useMembers.ts`'s own `MEMBERSHIP_FIXTURE` is the member-panel half).
    id: 'fixture-invitation-unrecognised-role',
    household_id: 'fixture-household',
    household_name: 'The Mehtas',
    email: 'diego.ramirez@example.com',
    role: 'co_owner',
    status: 'pending',
    created_at: '2026-08-12T09:00:00Z',
    expires_at: '2099-01-01T00:00:00Z',
    invited_by_user_id: 'fixture-user-priya',
    token: null,
    code: null,
  },
  {
    // The overflow row — the longest plausible address, matching
    // `MEMBERSHIP_FIXTURE`'s own long-email row, but a worse case here: this
    // row's timing phrase ("Sent N days ago · expires DATE") is the LONGEST
    // string `InvitationRow` renders, so pairing it with the longest email is
    // the actual worst case for a 320px viewport, not merely a long email on
    // its own.
    id: 'fixture-invitation-long-email',
    household_id: 'fixture-household',
    household_name: 'The Mehtas',
    email:
      'alexandra.montgomery-worthington.richardson@international-consulting-partners.example.com',
    role: 'member',
    status: 'pending',
    created_at: '2026-08-01T09:00:00Z',
    expires_at: '2099-01-01T00:00:00Z',
    invited_by_user_id: 'fixture-user-priya',
    token: null,
    code: null,
  },
]

/**
 * The four rows above, in the same `Panel` `Household.tsx` puts the
 * invitations panel in. **No `<Async>`, following `HouseholdMembersFixture`'s
 * own precedent above and for the identical reason**: this data is FIXED,
 * not a query, so there is no loading/error/stale to reach, and
 * `household-invitations` is a `live` surface (`useInvitations.ts`), so a
 * `stale` render would throw rather than pass silently. `InvitationRow` also
 * takes no `onResend`/`onRevoke` here, matching `Household.tsx`'s own
 * call site — Task 10 has not wired the real mutations yet, so this fixture
 * renders no actions rather than two dead ones (issue 315 finding N3).
 *
 * **The wire → `Invitation` map happens IN HERE, not at module scope** — the
 * exact issue #329 architect finding (B1) `HouseholdMembersFixture`'s own
 * docblock records: a top-level `.map()` whose callback calls an imported
 * function (`narrowRole`, inside `toInvitation`) is opaque to Rollup's purity
 * analysis, so the whole array would ship to production. `probe.build.test.ts`
 * covers this by prefix (`fixture-` is in `MARKERS`), not by name, so every
 * id above carries it.
 */
function HouseholdInvitationsFixture() {
  const invitations = INVITATION_FIXTURE.map(toInvitation)
  return (
    <Panel>
      {invitations.map((i) => (
        <InvitationRow key={i.id} invitation={i} />
      ))}
    </Panel>
  )
}

/**
 * The invite form, open — Task 9.
 *
 * **Unreachable to every sweep three times over**, which is one more than
 * `category-form` and for reasons that stack: `/household` is session-gated
 * (issue 161), the invitations panel it hangs off is gated again on
 * `canManageMembers`, and even past both the dialog only exists after a click
 * no sweep performs. `category-form` is the precedent AND the warning — the
 * M11 architecture review found a system-blue radio and a 13px tap target in
 * that form precisely because nothing could see it, and this form has three
 * radios of its own.
 *
 * Both lists are the ones already fixtured above, mapped **inside this
 * component** (issue #329 finding B1, `HouseholdMembersFixture`'s own
 * docblock): a module-scope `.map()` calling an imported function is opaque to
 * Rollup's purity analysis and ships the whole array to production.
 *
 * Passing the real lists rather than empty arrays is deliberate — it is what
 * makes `inviteBlockedReason` live on this screen, so typing a fixtured
 * address here renders the blocked field state a screenshot can catch.
 *
 * Only the FORM state is reachable here. The sent panel is the outcome of a
 * real 201 and no fixture can stand in for the token it carries, so its
 * layout remains unswept — the same limit `category-form` has.
 */
/** No opener exists on a probe screen — see the `restoreFocusTo` note below. */
const NO_OPENER = { current: null }

function InviteDialogFixture() {
  return (
    <InviteDialog
      open
      householdId="fixture-household"
      members={MEMBERSHIP_FIXTURE.map(toMember)}
      invitations={INVITATION_FIXTURE.map(toInvitation)}
      onClose={unavailable('onClose')}
      // Nothing opened this — the probe renders the dialog directly — so there
      // is no trigger to restore focus to. A detached ref is the honest value:
      // `Overlay` treats a null current exactly as it treats a removed node.
      restoreFocusTo={NO_OPENER}
    />
  )
}

/**
 * Task 13: twelve auto-rules — the populated `/rules` no sweep account can
 * reach (it has none). One paused, one read-only (a regex the builder cannot
 * edit), one with five actions and a three-value list, and long names.
 *
 * **Built inside the component, not at module scope** — an eager map keeps its
 * fixture in the production bundle (issue #329; `fixture-` is a build marker).
 * Ids carry that prefix. The head repeats `Rules`' own controls (tabs, filter,
 * New rule) because `Rules` fetches and the probe has no API.
 */
function RulesFixture() {
  const breakpoint = useBreakpoint()
  const layout = rowLayoutFor(breakpoint)
  const base = {
    user_id: 'fixture-user',
    stop_processing: true,
    apply_to_past: false,
    last_applied_at: null,
    description: null,
    is_enabled: true,
  }
  const cond = (field: string, operator: string, value: string) => [
    { logic_operator: 'and', conditions: [{ field, operator, value, case_sensitive: false }] },
  ]
  const set = (type: string, value: string) => ({ type, value })
  const specs: Array<Pick<Rule, 'name' | 'conditions' | 'actions'> & Partial<Rule>> = [
    { name: 'Coffee runs', conditions: cond('merchant_name', 'contains', 'Blue Bottle'), actions: [set('set_category', 'fixture-cat-coffee')] },
    { name: 'Weekend brunch', conditions: cond('day_of_week', 'is_in', '6,7'), actions: [set('add_tag', 'brunch')] },
    { name: 'Paused: old gym', is_enabled: false, conditions: cond('merchant_name', 'equals', 'Planet Fitness'), actions: [set('flag_for_review', '')] },
    {
      name: 'Regex from another device',
      conditions: [{ logic_operator: 'and', conditions: [{ field: 'merchant_name', operator: 'matches', value: '^AMZN.*', case_sensitive: false }] }],
      actions: [set('set_note', 'Amazon')],
    },
    {
      name: 'Everything at the corner shop with a deliberately long name that must truncate',
      conditions: cond('merchant_name_raw', 'is_in', 'SHELL OIL,CHEVRON,ARCO'),
      actions: [
        set('set_category', 'fixture-cat-fuel'),
        set('add_tag', 'car'),
        set('set_merchant', 'Gas station'),
        set('append_note', 'auto-filed'),
        set('exclude_from_reports', ''),
      ],
    },
    ...['Rent', 'Utilities', 'Transit', 'Streaming', 'Pharmacy', 'Gifts', 'Pets'].map((name, i) => ({
      name,
      times_applied: i * 7,
      conditions: cond('merchant_name', 'starts_with', name),
      actions: [set('add_tag', name.toLowerCase())],
    })),
  ]
  const reads = specs.map((sp, i) =>
    fromResponse({ ...base, id: `fixture-rule-${i + 1}`, priority: i + 1, times_applied: 12 + i, ...sp } as Rule),
  )
  const names = {
    category: (id: string) => (id === 'fixture-cat-coffee' ? 'Coffee Shops' : 'Fuel'),
    categoriesLoading: false,
    account: () => undefined,
    merchant: () => ({ status: 'found' as const, name: 'Gas station' }),
  }
  const noop = () => undefined
  return (
    <div className="probe-rules">
      <div className="rules-head">
        <div className="rules-head__title">
          <h2 className="rules-head__h">Auto-rules</h2>
          <p className="rules-head__count">11 active · 12 rules</p>
        </div>
        <Button variant="primary" size="page" onClick={noop}>
          ＋ New rule
        </Button>
      </div>
      <div className="rules-tools">
        <SegmentedControl
          label="Auto-rules view"
          name="probe-rules-tab"
          options={[
            { value: 'rules', label: 'Rules', count: 12 },
            { value: 'suggestions', label: 'Suggestions', count: 2 },
          ]}
          value="rules"
          onChange={noop}
        />
        <SearchField label="Filter rules" value="" onChange={noop} placeholder="Filter rules" />
      </div>
      <div className="rules-note">
        <span className="rules-note__badge" aria-hidden="true">
          1st
        </span>
        <div className="rules-note__text">
          <span className="rules-note__title">The first matching rule wins</span>
          <span className="rules-note__sub">Rules run top to bottom and stop at the first match.</span>
        </div>
      </div>
      <Panel className="rules-panel">
        <div className="rules-table">
          {layout !== 'phone' && (
            <div className={`rules-cols rules-cols--${layout}`} aria-hidden="true">
              <span className="rules-cols__pos">#</span>
              <span className="rules-cols__when">Rule · when</span>
              {layout === 'wide' && <span className="rules-cols__then">Then</span>}
              <span className="rules-cols__applied">Applied</span>
              <span className="rules-cols__on">On</span>
            </div>
          )}
          <ol className="rules-list">
            {reads.map((read, i) => (
              <RuleRow
                key={read.rule.id}
                read={read}
                position={i + 1}
                names={names}
                layout={layout}
                onEdit={noop}
                onToggle={noop}
                onDelete={noop}
                order={{ isFirst: i === 0, isLast: i === reads.length - 1, onMoveUp: noop, onMoveDown: noop }}
              />
            ))}
          </ol>
          <button type="button" className="rules-new" onClick={noop}>
            <span className="rules-new__plus" aria-hidden="true">
              ＋
            </span>
            <span className="rules-new__title">New rule</span>
            <span className="rules-new__hint">Match on merchant, description, account, date or note.</span>
          </button>
        </div>
      </Panel>
    </div>
  )
}

/**
 * A query cache that is already full and never fetches: the probe has no API,
 * and the two screens below read through TanStack Query. Everything they ask
 * for is primed by `fill`, and `staleTime: Infinity` means a primed entry is
 * never refetched — so nothing here touches the network, and what is measured
 * is the populated screen rather than its skeletons (issue #329).
 *
 * Created inside the component (`useState`'s initialiser), never at module
 * scope, for the reason every fixture in this file gives.
 */
function useFilledCache(fill: (client: QueryClient) => void): QueryClient {
  const [client] = useState(() => {
    const c = new QueryClient({
      defaultOptions: { queries: { staleTime: Infinity, gcTime: Infinity, retry: false } },
    })
    fill(c)
    return c
  })
  return client
}

/**
 * Fix round 1 (I4): the rule builder, open, on a populated draft — two
 * conditions (one a three-value list, so its hint line shows), two actions
 * (a category select and a text value), and a preview with rows. Nothing in a
 * browser measured this dialog before: `/rules` has no rules for a sweep
 * account, and the dialog only exists after a click no sweep performs.
 *
 * It is an EDIT, so the footer carries "Delete rule" as well. The handlers
 * throw, as every probe's do: nothing here saves.
 */
function RuleBuilderFixture() {
  const draft: RuleDraft = {
    id: 'fixture-rule-1',
    name: 'Grocery stores',
    enabled: true,
    groups: [
      {
        join: 'or',
        conditions: [
          { field: 'merchant_name', op: 'is_in', value: '', value2: '', list: ['Save-On-Foods', 'Safeway', 'Costco'] },
          { field: 'merchant_name_raw', op: 'starts_with', value: 'SOF', value2: '', list: [] },
          // The two chip-row conditions: a weekday set and an account set, so
          // the hit-target sweep measures their chips (issue 513, item 15).
          { field: 'day_of_week', op: 'is_in', value: '', value2: '', list: ['6', '7'] },
          { field: 'account_id', op: 'is_in', value: '', value2: '', list: ['fixture-account-1'] },
          // An amount between two values (#517): the direction segments and
          // both amount inputs, for the hit-target and contrast sweeps.
          { field: 'amount', op: 'between', value: '60', value2: '140', list: [], direction: 'any' },
        ],
      },
    ],
    actions: [
      { type: 'set_category', value: 'fixture-groceries' },
      { type: 'add_tag', value: 'weekly-shop' },
      // The merchant picker's field, named from the by-id cache entry below.
      { type: 'set_merchant', value: 'fixture-merchant-costco' },
    ],
    applyToPast: true,
    backfillStart: null,
  }
  const client = useFilledCache((c) => {
    c.setQueryData(keys.categories.list(), { categories: CATEGORIES_FIXTURE, most_used: [] })
    const account = (id: string, name: string, mask: string): Account => ({
      id, name, type: 'depository', subtype: 'checking', mask, provider: 'manual', institutionName: null,
      currentCents: null, availableCents: null, openingCents: null, currency: 'CAD', lastSyncedAt: null,
      ownerName: null, ownerId: null, errorCode: null, actionRequired: null, itemId: null,
      connectionStatus: null, reconnectable: false, statementDay: null,
    })
    const accounts: Account[] = [account('fixture-account-1', 'Chase Checking', '4242'), account('fixture-account-2', 'Everyday Savings', '9001')]
    c.setQueryData(keys.accounts.list(), accounts)
    c.setQueryData(keys.merchants.detail('fixture-merchant-costco'), {
      id: 'fixture-merchant-costco', name: 'Costco', type: 'business', user_id: 'fixture-user', household_id: null, icon: null,
      color: null, notes: null, logo_url: null, is_active: true, created_at: '2026-01-01T00:00:00Z',
      updated_at: '2026-01-01T00:00:00Z', is_global: false, plaid_merchant_entity_id: null, website: null,
    } satisfies Merchant)
    const write = toWrite(draft)
    const preview: PreviewResult = {
      matchedCount: 57,
      totalEvaluated: 1000,
      samples: [
        { transactionId: 'fixture-txn-1', merchantName: 'Save-On-Foods', date: '2026-09-26', amountCents: -8412, changes: [{}] },
        { transactionId: 'fixture-txn-2', merchantName: 'Costco Wholesale with a long trading name that must truncate', date: '2026-09-21', amountCents: -21490, changes: [{}] },
        { transactionId: 'fixture-txn-3', merchantName: null, date: '2026-09-14', amountCents: -1299, changes: [] },
      ],
    }
    // The key `RulePreview` asks for: the draft's own write body, limit 5.
    c.setQueryData(keys.rules.preview({ conditions: write.conditions, actions: write.actions, limit: 5 }), preview)
  })
  return (
    <QueryClientProvider client={client}>
      <RuleBuilder
        initial={draft}
        priorities={[1, 2, 3]}
        onClose={unavailable('onClose')}
        onSubmit={unavailable('onSubmit')}
        onDelete={unavailable('onDelete')}
      />
    </QueryClientProvider>
  )
}

/**
 * Fix round 1 (I4): the Suggestions tab with three cards, each with three
 * sample rows — one card's pattern and description long enough to have to
 * wrap and truncate. The list is handed to `RuleSuggestions` as a success
 * state; the samples come from the filled cache.
 */
function RuleSuggestionsFixture() {
  const specs: Array<[string, string, string, number, string]> = [
    ['1', 'SQ *PRADO', 'Food & Dining', 39, 'SQ *PRADO CAFE'],
    ['2', 'TRANSLINK', 'Transportation', 30, 'COMPASS VENDING TRANSLINK'],
    ['3', 'CLEARLY CONTACTS ONLINE ORDER', 'Health & Fitness', 6, 'CLEARLY *LASIK PLAN PAYMENT 4 OF 12 VANCOUVER BC'],
  ]
  const list: RuleSuggestion[] = specs.map(([n, pattern, categoryName, count]) => ({
    id: `fixture-suggestion-${n}`,
    pattern,
    categoryId: `fixture-cat-${n}`,
    categoryName,
    count,
    exampleIds: [1, 2, 3].map((k) => `fixture-sample-${n}-${k}`),
  }))
  const client = useFilledCache((c) => {
    for (const [n, , , , name] of specs) {
      for (const k of [1, 2, 3]) {
        const id = `fixture-sample-${n}-${k}`
        const sample: SuggestionSample = {
          id,
          name,
          date: `2026-09-${String(28 - k * 3 - Number(n)).padStart(2, '0')}`,
          amountCents: -(565 + k * 86 + Number(n) * 3300),
          currency: 'CAD',
        }
        c.setQueryData([...keys.ruleSuggestions.all(), 'sample', id], sample)
      }
    }
  })
  return (
    <QueryClientProvider client={client}>
      <div className="probe-rules">
        <RuleSuggestions state={{ status: 'success', data: list }} onCreate={unavailable('onCreate')} />
      </div>
    </QueryClientProvider>
  )
}

const SCREENS: {
  key: string
  label: string
  note: string
  /** A full-width page (not a centred card): the wrapper stretches, as the shell does. */
  fill?: boolean
  render: () => React.ReactNode
}[] = [
  {
    key: 'rules',
    label: 'Auto-rules — populated',
    note: 'Twelve rules: one paused, one read-only, one with five actions. Task 13, M11-R23.',
    fill: true,
    render: () => <RulesFixture />,
  },
  {
    key: 'rules-builder',
    label: 'Auto-rules — the builder, open on a populated rule',
    note:
      'Behind a click no sweep performs, on a route whose sweep account has no rules. Two ' +
      'conditions (one a list), two actions, the past-transactions checkbox on, and a preview ' +
      'with rows. Cache pre-filled; no network.',
    render: () => <RuleBuilderFixture />,
  },
  {
    key: 'rules-suggestions',
    label: 'Auto-rules — three suggestion cards with samples',
    note:
      'The Suggestions tab is empty for a sweep account, so its card grid was never measured. ' +
      'Three cards, three sample rows each, one with a pattern and description long enough to wrap.',
    fill: true,
    render: () => <RuleSuggestionsFixture />,
  },
  {
    key: 'crash',
    label: 'App crashed',
    note: 'The root ErrorBoundary fallback. Needs a thrown error to reach normally.',
    // A FIXED instant, not `new Date()`: a probe that renders a different
    // timestamp every load makes two screenshots differ for no reason, and the
    // point of this page is comparing pixels across changes.
    render: () => <AppCrashed name="Cashflow" at={new Date('2026-08-14T14:22:00Z')} />,
  },
  {
    key: 'maintenance-bare',
    label: 'Maintenance — nothing optional supplied',
    note: 'The honest floor: heading and body only. No time, no capability rows, no status link.',
    render: () => (
      <Maintenance
        heading="We're upgrading the sync engine"
        body="Balances may be a few minutes behind while this runs. Nothing you've entered is at risk, and no bank connection needs re-authorising afterwards."
      />
    ),
  },
  {
    key: 'maintenance-full',
    label: 'Maintenance — every optional field supplied',
    note: 'What the backend contract on issue 274 would produce at its richest.',
    render: () => (
      <Maintenance
        heading="We're upgrading the sync engine"
        body="Balances may be a few minutes behind while this runs. Nothing you've entered is at risk, and no bank connection needs re-authorising afterwards."
        returnEta="Back at 03:30 PDT"
        statusUrl="https://status.tracke5.com"
        capabilities={[
          { label: 'Reading your data', statusLabel: 'Working', available: true },
          { label: 'Fresh bank sync', statusLabel: 'Paused', available: false },
          { label: 'CSV import', statusLabel: 'Paused', available: false },
        ]}
      />
    ),
  },
  {
    key: 'notfound',
    label: '404',
    note: 'Normally behind RequireAuth, so it needs a session AND a bad URL. Renders without the chrome here — the real one has a sidebar around it.',
    render: () => <NotFound />,
  },
  {
    key: 'verify-email',
    label: 'Check your inbox',
    note: 'Needs a signed-in, unverified user. The AuthContext below supplies one.',
    render: () => (
      <AuthContext.Provider value={SIGNED_IN}>
        <VerifyEmail />
      </AuthContext.Provider>
    ),
  },
  {
    key: 'categories',
    label: 'Categories — a parent, its children, and a user category',
    note: 'Session-gated normally (issue 161). Fixed fixture: the tree shape, a null colour, and the longest seeded name.',
    render: () => <CategoriesFixture />,
  },
  {
    key: 'category-form',
    label: 'New category — the glass dialog, open',
    note:
      'Doubly unreachable otherwise: session-gated AND behind an entitlement, so no sweep ' +
      'has ever measured it. The M11 architecture review found a system-blue radio and a ' +
      '13px tap target in this form precisely because nothing could see it.',
    render: () => <CategoryFormFixture />,
  },
  {
    key: 'household-members',
    label: 'Household members — five rows, an out-of-enum role, and the email that overflowed',
    note:
      'Session-gated normally (issue 161), and previously unreachable to every sweep for a ' +
      'different reason: with no API reachable, /household renders Async skeletons, never a ' +
      'member row (issue 329). Fixed fixture: the null-name/long-email row that measured ' +
      'document.scrollWidth 460px at a 320px viewport, and role co_owner rendered verbatim.',
    render: () => <HouseholdMembersFixture />,
  },
  {
    key: 'household-invitations',
    label: 'Household invitations — pending, expired, an out-of-enum role, and the longest email',
    note:
      'Session-gated normally (issue 161), and previously unreachable for the same reason ' +
      'household-members was (issue 329): with no API reachable, /household renders Async ' +
      'skeletons, never an invitation row. Fixed fixture: an expired row shaped the way ' +
      "production actually produces it (status: 'pending', expires_at in the past — issue " +
      '315), role co_owner rendered verbatim, and the longest plausible email paired with the ' +
      "row's own longest timing phrase.",
    render: () => <HouseholdInvitationsFixture />,
  },
  {
    key: 'invite-dialog',
    label: 'Invite someone — the glass form, open, over both fixtured lists',
    note:
      'Triply unreachable otherwise: /household is session-gated (issue 161), the ' +
      'invitations panel is gated again on canManageMembers, and the dialog only exists ' +
      'after a click no sweep performs. category-form is the precedent and the warning — ' +
      'M11 found a system-blue radio and a 13px tap target in that form because nothing ' +
      'could see it, and this one has three radios. The FORM state only; the sent panel ' +
      'needs a real 201 and stays unswept.',
    render: () => <InviteDialogFixture />,
  },
  {
    key: 'cashflow',
    label: 'Cashflow — premium, one selected period (M3)',
    note: 'Premium fixture, today 2026-10-13, October 2026 selected (partial). The period figures stay fixed as the period moves.',
    fill: true,
    render: () => <CashflowFixture kind="premium" />,
  },
  {
    key: 'cashflow-free',
    label: 'Cashflow — free: tiles, one upgrade prompt, a locked chart and Where it went',
    note: 'Review Focus 3. An empty entitlements provider, so the gates lock as they would for a free user.',
    fill: true,
    render: () => <CashflowFixture kind="free" />,
  },
  {
    key: 'cashflow-new',
    label: 'Cashflow — a three-week account',
    note: 'Review Focus 4: no prior to compare, no complete period yet.',
    fill: true,
    render: () => <CashflowFixture kind="new" />,
  },
  {
    key: 'cashflow-empty',
    label: 'Cashflow — accounts, nothing in this period',
    note: 'Totals all zero: "Nothing moved in July 2026.", no action.',
    fill: true,
    render: () => <CashflowFixture kind="empty" />,
  },
  {
    key: 'cashflow-no-accounts',
    label: 'Cashflow — no accounts',
    note: 'account_count 0: the empty that links to Accounts.',
    fill: true,
    render: () => <CashflowFixture kind="no-accounts" />,
  },
  {
    key: 'cashflow-error',
    label: 'Cashflow — a contract error',
    note: 'A float amount, parsed through toCashflow: one error, one Try again.',
    fill: true,
    render: () => <CashflowFixture kind="error" />,
  },
  {
    key: 'cashflow-detail',
    label: 'Cashflow drill-down — one category, September 2026',
    note: 'Crumb, name, period header, a one-sided chart, three stats, four transactions over two days. Fixture figures.',
    fill: true,
    render: () => <CashflowDetailFixture flagged={false} />,
  },
  {
    key: 'cashflow-no-merchant',
    label: 'Cashflow drill-down — No merchant, September 2026',
    note: 'Four rows with no merchant, three sharing one long bank description: the "Set all 3 at once?" prompt above the list, and every row carrying "Needs merchant".',
    fill: true,
    render: () => <CashflowDetailFixture noMerchant />,
  },
  {
    key: 'cashflow-detail-flagged',
    label: 'Cashflow drill-down — a flagged category',
    note: 'Scoped expense −$480: the note and its link to Categories, a negative Spent, no Share tile.',
    fill: true,
    render: () => <CashflowDetailFixture flagged />,
  },
  {
    key: 'premium-gate',
    label: 'Premium gate — locked, over the control it covers',
    note:
      'Unreachable in development BY CONSTRUCTION: TIER resolves to premium there, so ' +
      'entitlementsForTier grants all seven and every gate renders unlocked — nobody sees ' +
      'this locked branch without a free-tier build or override. That is how a CTA shipped ' +
      'as an ellipse with its text outside the fill. Rendered here with an EMPTY provider, ' +
      'over M11\'s real preview.',
    render: () => <PremiumGateFixture />,
  },
  {
    key: 'button-preview',
    label: 'ButtonPreview — beside the live Button, per variant and size',
    note:
      'Dev-only measuring surface: a locked gate shows a span in a live button\'s ' +
      'place, and only a browser can tell whether it lays out the same. Block wrappers ' +
      'carry data-pair and data-kind.',
    render: () => <ButtonPreviewFixture />,
  },
]

/**
 * A signed-in user, assembled by hand.
 *
 * `AuthContext` rather than nesting a second `<AuthProvider port={fake}>`:
 * that provider bootstraps a session over the network on mount, which is a lot
 * of machinery to stand up for a screen that reads `auth.user.email` and calls
 * one action. `RequireAuth.test.tsx` builds the same shape for the same reason.
 *
 * Every action throws rather than returning a resolved promise. A probe is for
 * LOOKING at a screen; if pressing something here silently succeeded it would
 * imply a working flow that has not been wired, and the next person would
 * report a bug against the wrong layer. `sendVerificationEmail` is the one
 * exception — it resolves, because the resend cooldown is the whole behaviour
 * worth watching on this screen and it cannot be watched without it.
 */
const unavailable = (name: string) => () => {
  throw new Error(`${name}() is not wired on the probe page — this is a rendering door only`)
}

const SIGNED_IN: AuthContextValue = {
  status: 'signedIn',
  user: { uid: 'probe', email: 'sam@example.test', emailVerified: false },
  profile: { is_onboarded: true } as AuthContextValue extends { profile: infer P } ? P : never,
  redirectError: null,
  signIn: unavailable('signIn'),
  signUp: unavailable('signUp'),
  signInWithGoogle: unavailable('signInWithGoogle'),
  signOut: unavailable('signOut'),
  sendPasswordReset: unavailable('sendPasswordReset'),
  verifyResetCode: unavailable('verifyResetCode'),
  completeReset: unavailable('completeReset'),
  sendVerificationEmail: async () => {},
  retrySession: unavailable('retrySession'),
  clearRedirectError: () => {},
  enrolledFactors: () => [],
  identity: () => ({ providers: [], unrecognised: [], phoneNumber: null }),
  markOnboarded: unavailable('markOnboarded'),
  beginTotpEnrolment: unavailable('beginTotpEnrolment'),
  beginPhoneEnrolment: unavailable('beginPhoneEnrolment'),
  removeFactor: unavailable('removeFactor'),
  reauthMethod: () => null,
  reauthenticate: unavailable('reauthenticate'),
}

export function Probe() {
  const [params] = useSearchParams()
  const key = params.get('screen')
  const chosen = SCREENS.find((s) => s.key === key)

  if (chosen) return <div className={chosen.fill ? 'probe-frame' : undefined} data-probe={PROBE_SENTINEL}>{chosen.render()}</div>

  return (
    <div className="probe" data-probe={PROBE_SENTINEL}>
      <h1 className="probe__title">Screen probe</h1>
      <p className="probe__lede">
        Dev-only. These screens cannot be reached in a browser by ordinary navigation, so nothing
        that looks at rendered pixels can see them. Each link renders one on its own.
      </p>
      <ul className="probe__list">
        {SCREENS.map((s) => (
          <li key={s.key} className="probe__item">
            <Link className="probe__link" to={`?screen=${s.key}`}>
              {s.label}
            </Link>
            <span className="probe__note">{s.note}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
